import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, Checkbox, FormControlLabel, Icon } from '@mui/material'
import SchemaTable from '../table/SchemaTable'
import FormattedDate from '../table/FormattedDate'
import StatusBadge from '../StatusBadge'
import { resolveJournalRow } from './resolveJournalRow'

// An info/error indicator for the record's severity: a red mark for the codes that
// report an exclusion or invalidation, a blue mark for everything else.
export function SeverityBadge({ severity }) {
    const isError = severity === 'error'
    return (
        <Icon color={isError ? 'error' : 'info'} fontSize="small">
            {isError ? 'error' : 'info'}
        </Icon>
    )
}

// A single-character divider between adjacent items on the items line, with wide
// horizontal padding. A plain span so it inherits the row's font size.
function ItemSeparator() {
    return (
        <Box component="span" sx={{ px: 1.5, color: 'text.secondary' }}>
            |
        </Box>
    )
}

// The concrete items a record points at — entry link, document link, image class
// label, transmission — laid out inline so the row stays compact; each item is an
// inline block and nothing is forced onto its own line. Labels are plain spans so
// they inherit the row's font size, matching the links. The document link rests on
// the document's own entry/company, independent of the record's entry link. An
// image and a transmission are labels, never links.
export function SubjectCell({ row }) {
    const {
        entry,
        company,
        document,
        documentEntry,
        documentCompany,
        documentLabel,
        imageLabel,
        batch,
    } = row

    const items = []
    if (entry != null && company != null) {
        items.push(
            <Fragment key="entry">
                <Link to={`/companies/${company.id}/entries/${entry.id}`}>
                    {`רשומה ${entry.id}`}
                </Link>
                {entry.state != null && (
                    <StatusBadge status={entry.state} sx={{ marginInlineStart: 1 }} />
                )}
            </Fragment>,
        )
    }
    if (document != null && documentEntry != null && documentCompany != null) {
        items.push(
            <Link
                key="document"
                to={`/companies/${documentCompany.id}/entries/${documentEntry.id}/documents/${document.id}`}
            >
                {documentLabel}
            </Link>,
        )
    }
    if (imageLabel != null) {
        items.push(<span key="image">{imageLabel}</span>)
    }
    if (batch != null) {
        items.push(<span key="batch">{`שידור ${batch.id}`}</span>)
    }

    return (
        <Box>
            {items.map((item, index) => (
                <Fragment key={item.key}>
                    {index > 0 && <ItemSeparator />}
                    <Box component="span" sx={{ display: 'inline-block' }}>
                        {item}
                    </Box>
                </Fragment>
            ))}
        </Box>
    )
}

// Accessors return the visible text so both column sorting and the search box's
// partial matching act on what the cell renders; the Cell renders the rich version.
const severityColumn = {
    Header: '',
    accessor: 'severity',
    enableSorting: false,
    align: 'center',
    width: '1rem',
    Cell: (cell) => <SeverityBadge severity={cell.row.original.severity} />,
}

const idColumn = {
    Header: 'אירוע',
    accessor: 'id',
    width: '4rem',
}

const parentColumn = {
    Header: 'שרשור',
    accessor: 'parentId',
    width: '4rem',
}

const dateColumn = {
    Header: 'תאריך',
    accessor: 'createdAt',
    sortDescFirst: true,
    width: '9rem',
    Cell: (cell) => <FormattedDate value={cell.row.original.createdAt} />,
}

const companyColumn = {
    Header: 'חברה',
    accessor: 'companyName',
    Cell: (cell) => {
        const { company } = cell.row.original
        return company != null
            ? <Link to={`/companies/${company.id}/entries`}>{company.companyName}</Link>
            : null
    },
}

const subjectColumn = {
    Header: 'פריטים',
    accessor: 'subject',
    Cell: (cell) => <SubjectCell row={cell.row.original} />,
}

const messageColumn = {
    Header: 'תיאור',
    accessor: 'message',
}

const typeColumn = {
    Header: 'קוד אירוע',
    accessor: 'type',
}

export default function JournalTable({ records }) {
    // Both off by default: only root records show, and the thread/type-code columns
    // are hidden.
    const [showThreads, setShowThreads] = useState(false)
    const [showTypeCode, setShowTypeCode] = useState(false)

    const allRows = (records ?? []).map((record) => ({
        id: record.id,
        ...resolveJournalRow(record),
    }))
    const rows = showThreads
        ? allRows
        : allRows.filter((row) => row.parentId == null)

    const columns = [
        idColumn,
        ...(showThreads ? [parentColumn] : []),
        dateColumn,
        companyColumn,
        subjectColumn,
        severityColumn,
        messageColumn,
        ...(showTypeCode ? [typeColumn] : []),
    ]

    const toolbarActions = (
        <>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={showThreads}
                        onChange={(event) => setShowThreads(event.target.checked)}
                    />
                }
                label="שרשור"
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={showTypeCode}
                        onChange={(event) => setShowTypeCode(event.target.checked)}
                    />
                }
                label="קוד אירוע"
            />
        </>
    )

    return (
        <SchemaTable
            data={rows}
            columns={columns}
            canSearch={true}
            toolbarActions={toolbarActions}
        />
    )
}
